// @ts-strict-ignore
import * as React from 'react';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { OverviewDestinationStatus, DestinationStatusOrderIndices } from '@/types/workflowOverview';
import {
    tagColorForOverviewDestinationStatus,
    displayTextForOverviewDestinationStatus,
    displayStringForDestination,
} from '@/helpers/displayHelpers';
import { useMdmPlatformOverride } from '@/helpers/temporaryMdmDisplayOverride';
import { groupBy, keys, sortBy } from 'lodash';
import { ClockCircleOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import Divider from 'antd/lib/divider';
import { Duration } from 'dayjs/plugin/duration';
import { dayjs } from '@/extendedDayjs';
import List from 'antd/lib/list';
import { DestinationIcon } from '@/components/sidebar/shared/components';
import Popover from 'antd/lib/popover';

export interface DestinationStatusDataSourceItem {
    destinationStepId: string;
    destination: Destination;
    status: OverviewDestinationStatus;
    displayText: string;
    maybeReviewStartedAt?: string;
}

interface DestinationStatusTagWidgetProps {
    statusDataSource: DestinationStatusDataSourceItem[];
    allDestinations: Destination[];
}

export const DestinationStatusTagWidget: React.FC<DestinationStatusTagWidgetProps> = ({ statusDataSource }) => {
    // Statuses to exclude from the destination status widget. Most of these
    // will still be reflected in the tooltip surfaced by the main overview status.

    const widgetExcludeStatuses: OverviewDestinationStatus[] = [
        OverviewDestinationStatus.Inactive,
        OverviewDestinationStatus.Skipped,
        OverviewDestinationStatus.Upcoming,
        OverviewDestinationStatus.Preparing,
        OverviewDestinationStatus.Missing,
    ];

    const filteredStatusDataSource = statusDataSource.filter((statusDatum) => {
        return !widgetExcludeStatuses.includes(statusDatum.status);
    });

    // We use the keys returned by groupBy's callback to group status datum by their display text (which can differ by platform, app store type, and other details).
    // • Ex, if we have two phased rollouts in progress with different rollout percentages, we group them
    //   separately, otherwise we group them together, etc.
    // • Similarly, if we have two "In review" statuses, group them either together or separately based o
    //   whether they started review at the same time.
    // • This paradigm allows us to condense identical destination statuses within the view, while leaving
    //   distinct ones intact.

    const groupedByStatusSource = groupBy(filteredStatusDataSource, (statusDatum) => {
        const datumDisplayText = statusDatum.displayText;

        let datumReviewStartedAt = '';
        if (statusDatum.status === OverviewDestinationStatus.InReview && !!statusDatum.maybeReviewStartedAt) {
            datumReviewStartedAt = statusDatum.maybeReviewStartedAt;
        }

        return `${datumDisplayText}&maybeReviewStartedAt=${datumReviewStartedAt}`;
    });

    const groupKeys = keys(groupedByStatusSource);
    const sortedKeys = sortBy(groupKeys, (groupKey) => {
        const groupStatus = groupedByStatusSource[groupKey][0].status;
        return DestinationStatusOrderIndices[groupStatus];
    });

    const now = dayjs();

    return (
        <div
            style={{
                maxWidth: '50%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignContent: 'center',
                rowGap: 18,
                flexWrap: 'wrap',
            }}
        >
            {sortedKeys.map((groupKey, i) => {
                const dataInGroup = groupedByStatusSource[groupKey];

                if (!dataInGroup || dataInGroup.length === 0) {
                    return null;
                }

                const groupDestinations = dataInGroup.map((groupDatum) => groupDatum.destination);

                const shouldRenderDivider = i > 0;

                const groupKeyParts = groupKey.split('&maybeReviewStartedAt=');

                const displayTextOverride = groupKeyParts[0];

                const maybeReviewStartedAt = groupKeyParts.length > 1 && !!groupKeyParts[1] ? groupKeyParts[1] : null;

                let maybeReviewDuration: Duration;
                if (maybeReviewStartedAt && dayjs(maybeReviewStartedAt).isValid()) {
                    const reviewStartedAtMoment = dayjs(maybeReviewStartedAt);
                    const duration = dayjs.duration(now.diff(reviewStartedAtMoment));
                    maybeReviewDuration = duration;
                }

                let maybeDurationString: string;
                if (maybeReviewDuration) {
                    const totalHrs = maybeReviewDuration.days() * 24 + maybeReviewDuration.hours();
                    const durationMinutes = maybeReviewDuration.minutes();

                    maybeDurationString = `${totalHrs}h ${durationMinutes}m`;
                }

                return (
                    <div
                        key={groupKey}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                        }}
                    >
                        {shouldRenderDivider && (
                            <Divider
                                type="vertical"
                                style={{
                                    height: '100%',
                                    top: 0,
                                    backgroundColor: antGrey.grey4,
                                    margin: '0px 16px',
                                }}
                            />
                        )}
                        <div style={{ marginRight: '12px' }}>
                            <DestinationCounter destinations={groupDestinations} size={24} />
                        </div>
                        <DestinationStatusTagWithPopover
                            destinationStatusDataSourceItems={dataInGroup}
                            displayTextOverride={displayTextOverride}
                            shouldRenderStatusData={false}
                        />
                        {!!maybeDurationString && (
                            <div
                                style={{
                                    color: antGrey.grey7,
                                    marginLeft: 12,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <ClockCircleOutlined
                                    style={{
                                        verticalAlign: 'middle',
                                        color: antGrey.grey6,
                                    }}
                                />
                                {` ${maybeDurationString}`}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

interface DestinationStatusTagProps {
    destinationStatus: OverviewDestinationStatus;
    showDestinationIcon: boolean;
    shouldRenderEllipsis: boolean;
    displayTextOverride?: string;
    destinations?: Destination[];
}

export const DestinationStatusTag: React.FC<DestinationStatusTagProps> = ({
    destinationStatus,
    showDestinationIcon,
    shouldRenderEllipsis,
    displayTextOverride,
    destinations,
}) => {
    let statusDisplayText: string;

    if (displayTextOverride) {
        statusDisplayText = displayTextOverride;
    } else {
        const maybePlatform = destinations?.length > 1 ? destinations[0].platform : undefined;
        const maybeStoreType = destinations?.length > 1 ? destinations[0].appStoreType : undefined;

        statusDisplayText = displayTextForOverviewDestinationStatus(destinationStatus, maybePlatform, maybeStoreType);
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
            }}
        >
            {showDestinationIcon && !!destinations && destinations.length > 0 && (
                <DestinationCounter destinations={destinations} size={24} />
            )}
            <Tag
                style={{
                    ...titleStyle,
                    fontSize: 10,
                    lineHeight: '22px',
                    margin: 0,
                    color: `${tagColorForOverviewDestinationStatus(destinationStatus)}`,
                    background: runwayColors.white,
                    border: `1px solid ${tagColorForOverviewDestinationStatus(destinationStatus)}`,
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: '100%',
                        textAlign: 'center',
                        position: 'relative',
                        top: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        justifyItems: 'center',
                        alignItems: 'stretch',
                        gap: 8,
                    }}
                >
                    {statusDisplayText}
                    {shouldRenderEllipsis && (
                        <EllipsisOutlined
                            color={runwayColors.white}
                            style={{
                                fontSize: '16px',
                            }}
                        />
                    )}
                </div>
            </Tag>
        </div>
    );
};

interface DestinationStatusTagWithPopoverProps {
    destinationStatusDataSourceItems: DestinationStatusDataSourceItem[];
    displayTextOverride?: string;
    shouldRenderStatusData?: boolean;
}

export const DestinationStatusTagWithPopover: React.FC<DestinationStatusTagWithPopoverProps> = ({
    destinationStatusDataSourceItems,
    displayTextOverride,
    shouldRenderStatusData,
}) => {
    const status = destinationStatusDataSourceItems[0].status;
    const destinations = destinationStatusDataSourceItems.map((item) => item.destination);
    return (
        <Popover
            content={
                <DestinationStatusPopoverContent
                    destinationStatusDataSource={destinationStatusDataSourceItems}
                    shouldRenderStatusData={shouldRenderStatusData}
                />
            }
            trigger="hover"
        >
            <>
                <DestinationStatusTag
                    destinationStatus={status}
                    displayTextOverride={displayTextOverride}
                    showDestinationIcon={false}
                    shouldRenderEllipsis={destinations.length > 1}
                />
            </>
        </Popover>
    );
};

export interface DestinationStatusPopoverContentProps {
    destinationStatusDataSource: DestinationStatusDataSourceItem[];
    shouldRenderStatusData: boolean;
}

export const DestinationStatusPopoverContent: React.FC<DestinationStatusPopoverContentProps> = ({
    destinationStatusDataSource,
    shouldRenderStatusData,
}) => {
    const now = dayjs();
    const mdmPlatformLabel = useMdmPlatformOverride()?.label;
    return (
        <List
            style={{
                maxHeight: '40vh',
                overflowY: 'auto',
                padding: '6px 6px 6px 6px',
                minWidth: '100px',
            }}
            split={false}
            dataSource={destinationStatusDataSource}
            renderItem={(destStatusDatum, i) => {
                const isLast = i === destinationStatusDataSource.length - 1;
                const shouldRenderDivider = destinationStatusDataSource.length > 1 && !isLast;

                const destination = destStatusDatum.destination;
                const destStatus = destStatusDatum.status;
                const displayText = destStatusDatum.displayText;

                const isMissingStatus =
                    destStatus === OverviewDestinationStatus.Missing || destStatus == ('' as OverviewDestinationStatus);
                const isInReviewStatus = destStatus === OverviewDestinationStatus.InReview;

                const maybeReviewStartedAt = destStatusDatum.maybeReviewStartedAt;

                let maybeReviewDuration: Duration;
                if (maybeReviewStartedAt && dayjs(maybeReviewStartedAt).isValid()) {
                    const reviewStartedAtMoment = dayjs(maybeReviewStartedAt);
                    const duration = dayjs.duration(now.diff(reviewStartedAtMoment));
                    maybeReviewDuration = duration;
                }

                let maybeDurationString: string;
                if (maybeReviewDuration) {
                    const totalHrs = maybeReviewDuration.days() * 24 + maybeReviewDuration.hours();
                    const durationMinutes = maybeReviewDuration.minutes();

                    maybeDurationString = `${totalHrs}h ${durationMinutes}m`;
                }

                return (
                    <List.Item
                        key={destination.stepConfigId}
                        style={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                width: '100%',
                                gap: 16,
                            }}
                        >
                            <Text
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 8,
                                    color: antGrey.grey8,
                                    fontSize: 14,
                                    fontWeight: 300,
                                }}
                            >
                                <DestinationIcon destination={destination} size={24} />
                                {displayStringForDestination(
                                    destination.platform,
                                    destination.appStoreType,
                                    destination.name,
                                    destination.appName,
                                    mdmPlatformLabel,
                                )}
                            </Text>
                            {shouldRenderStatusData && !isMissingStatus && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                    }}
                                >
                                    <DestinationStatusTag
                                        destinationStatus={destStatus}
                                        displayTextOverride={displayText}
                                        showDestinationIcon={false}
                                        shouldRenderEllipsis={false}
                                    />
                                    {isInReviewStatus && !!maybeDurationString && (
                                        <div
                                            style={{
                                                color: antGrey.grey7,
                                                marginLeft: 12,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            }}
                                        >
                                            <ClockCircleOutlined
                                                style={{
                                                    verticalAlign: 'middle',
                                                    color: antGrey.grey6,
                                                }}
                                            />
                                            {` ${maybeDurationString}`}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {shouldRenderDivider && (
                            <Divider
                                style={{
                                    margin: '16px 0px',
                                }}
                            />
                        )}
                    </List.Item>
                );
            }}
        ></List>
    );
};
